// The home dashboard: one status sentence, decisions waiting, visits, requests and the kill switch.
import { api } from "../lib/api";
import { brand } from "../lib/brand";
import { OCCUPANCY } from "../lib/catalog";
import { fmt, t, tl } from "../lib/i18n";
import { Link, navigate } from "../lib/router";
import { h, invalidate, resource } from "../lib/ui";
import {
  act, Async, Button, confirmDialog, Dialog, Field, formData, HealthChip, isBusy, openDialog, Select, TicketStatus,
} from "../shared/components";
import { Icon, Roofline } from "../shared/icons";

function placeName(home: any) {
  return home.nickname || home.address_line1;
}

function occupancyDialog(home: any, reload: () => void) {
  const today = new Date().toISOString().slice(0, 10);
  openDialog((close) => (
    <Dialog title={t("customer.occupancy.title")} onClose={close}>
      <form class="stack" onSubmit={(e: Event) => {
        const d = formData(e);
        act("occ.save", async () => {
          await api.post(`/api/my/homes/${home.id}/occupancy`, { occupancy: d.occupancy, since: d.since });
          close();
          reload();
        }, t("customer.occupancy.saved"));
      }}>
        <p class="small muted">{t("customer.occupancy.body")}</p>
        <Field label={t("fields.occupancy")} id="occ"><Select id="occ" name="occupancy" value={home.occupancy} options={OCCUPANCY.map((x) => ({ value: x, label: t(`occupancy.${x}`) }))} /></Field>
        <Field label={t("customer.occupancy.since")} id="occ-since"><input class="input" id="occ-since" name="since" type="date" value={today} required /></Field>
        <div class="note small">{t("customer.occupancy.insurance")}</div>
        <div class="row end"><Button kind="secondary" onClick={close}>{t("common.cancel")}</Button><Button type="submit" busy={isBusy("occ.save")}>{t("common.save")}</Button></div>
      </form>
    </Dialog>
  ));
}

export function Dashboard(props: { homeId: string }) {
  const r = resource(`my.dash.${props.homeId}`, () => api.get(`/api/my/homes/${props.homeId}/dashboard`));
  const reload = () => invalidate(`my.dash.${props.homeId}`);
  return Async(r, (d: any) => {
    const { home, me, visits, tickets } = d;
    const p = me.perms;
    const now = Date.now();
    const past = visits.filter((v: any) => v.status === "completed").sort((a: any, b: any) => +new Date(b.scheduled_for) - +new Date(a.scheduled_for));
    const upcoming = visits.filter((v: any) => new Date(v.scheduled_for).getTime() > now - 3600_000 && !["completed", "cancelled"].includes(v.status));
    const last = past[0];
    const next = upcoming[0];
    const needEntry = p.approveEntry ? upcoming.filter((v: any) => ["scheduled", "notified", "time_confirmed"].includes(v.status)) : [];
    const estimates = tickets.filter((x: any) => x.status === "awaiting_approval" && x.pending_estimate);
    const requests = p.approveRequests ? tickets.filter((x: any) => x.status === "awaiting_resident_approval") : [];
    const signoffs = p.approveRequests ? tickets.filter((x: any) => x.status === "awaiting_signoff") : [];
    const open = tickets.filter((x: any) => !["closed", "cancelled"].includes(x.status));
    const decisions = estimates.length + requests.length + signoffs.length + needEntry.length;
    const place = placeName(home);

    const approveEntry = (v: any) => act(`entry.${v.id}`, async () => {
      await api.post(`/api/my/visits/${v.id}/approve-entry`);
      reload();
    }, t("customer.entryApproved"));

    const killSwitch = async () => {
      const ok = await confirmDialog({ title: t("customer.kill.confirmTitle"), body: t("customer.kill.confirmBody", { app: brand.current.appName }), confirm: t("customer.kill.confirm"), danger: true });
      if (!ok) return;
      act("kill", async () => {
        await api.post(`/api/my/homes/${home.id}/kill-switch`);
        reload();
      }, t("customer.kill.done"));
    };
    const restore = () => act("restore", async () => {
      await api.post(`/api/my/homes/${home.id}/restore-access`);
      reload();
    }, t("customer.kill.restored"));

    return (
      <div>
        {home.access_revoked_at ? (
          <div class="revoked-banner" role="alert">
            <div class="row" style="gap:10px"><Icon name="power" /><strong>{t("customer.kill.activeTitle")}</strong></div>
            <span class="small">{t("customer.kill.activeBody", { when: fmt.dateTime(home.access_revoked_at), app: brand.current.appName })}</span>
            {p.manageMembers ? <div><Button kind="secondary" size="small" busy={isBusy("restore")} onClick={restore}>{t("customer.kill.restore")}</Button></div> : null}
          </div>
        ) : null}

        <section class={`plate ${home.health} ${home.photo_file_id ? "with-photo" : ""}`} aria-label={t("customer.statusLabel")}>
          {home.photo_file_id
            ? <img class="plate-photo" src={`/api/files/${home.photo_file_id}`} alt={t("admin.homes.photoAlt", { address: home.address_line1 })} />
            : <Roofline class="roofline" />}
          <div class="where">{home.nickname ? `${home.address_line1}, ${home.city}` : home.city}</div>
          <h1>{t(`customer.status.${home.health}`, { place })}</h1>
          <div class="meta">
            <span>{last ? t("customer.lastVisit", { date: fmt.date(last.scheduled_for, "short"), name: last.expert ?? "" }) : t("customer.noVisitYet")}</span>
            {next ? <span>{t("customer.nextVisit", { date: fmt.date(next.scheduled_for, "weekday"), time: fmt.time(next.scheduled_for) })}</span> : null}
            {home.occupancy !== "occupied" ? <span>{t(`occupancy.${home.occupancy}`)}</span> : null}
          </div>
        </section>

        <div class="quick">
          {p.request ? <Link href="/request"><Icon name="tool" />{t("customer.quick.request")}</Link> : null}
          <Link href="/people"><Icon name="users" />{t("customer.quick.people")}</Link>
          {p.bodycam ? <Link href="/videos"><Icon name="video" />{t("customer.quick.videos", { count: d.videoCount })}</Link> : (
            p.setOccupancy ? <button onClick={() => occupancyDialog(home, reload)}><Icon name="home" />{t("customer.quick.occupancy")}</button> : null
          )}
        </div>

        {decisions ? (
          <div>
            <div class="section-title"><h2>{t("customer.waiting", { count: decisions })}</h2></div>
            <div class="stack">
              {estimates.map((x: any) => (
                <div class="decision">
                  <div class="row between top">
                    <div class="stack tight grow" style="gap:2px">
                      <span class="strong">{x.title}</span>
                      <span class="small muted">{x.pending_estimate.description ?? t(`service.${x.service}`)}</span>
                    </div>
                    {x.pending_estimate.amount_cents !== undefined ? <span class="amount">{fmt.money(x.pending_estimate.amount_cents)}</span> : null}
                  </div>
                  <div class="row"><Button size="small" onClick={() => navigate(`/requests/${x.id}`)}>{t("customer.reviewQuote")}</Button></div>
                </div>
              ))}
              {needEntry.map((v: any) => (
                <div class="decision">
                  <div class="stack tight" style="gap:2px">
                    <span class="strong">{t("customer.entryTitle", { type: t(`visitType.${v.type}`) })}</span>
                    <span class="small muted">{t("customer.entryBody", { date: fmt.date(v.scheduled_for, "weekday"), time: fmt.time(v.scheduled_for), name: v.expert ?? "" })}</span>
                  </div>
                  <div class="row"><Button size="small" busy={isBusy(`entry.${v.id}`)} disabled={!!home.access_revoked_at} onClick={() => approveEntry(v)}>{t("customer.approveEntry")}</Button></div>
                </div>
              ))}
              {requests.map((x: any) => (
                <div class="decision">
                  <span class="strong">{t("customer.requestToApprove", { title: x.title })}</span>
                  <div class="row"><Button size="small" kind="secondary" onClick={() => navigate(`/requests/${x.id}`)}>{t("customer.review")}</Button></div>
                </div>
              ))}
              {signoffs.map((x: any) => (
                <div class="decision">
                  <span class="strong">{t("customer.signoffTitle", { title: x.title })}</span>
                  <div class="row"><Button size="small" kind="secondary" onClick={() => navigate(`/requests/${x.id}`)}>{t("customer.signoffAction")}</Button></div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div class="section-title"><h2>{t("customer.upcoming")}</h2></div>
        {upcoming.length ? (
          <div class="list">
            {upcoming.slice(0, 4).map((v: any) => (
              <div class="list-row">
                <div class="when"><b>{new Intl.DateTimeFormat(document.documentElement.lang, { day: "numeric", timeZone: "America/Vancouver" }).format(new Date(v.scheduled_for))}</b><span>{new Intl.DateTimeFormat(document.documentElement.lang, { month: "short", timeZone: "America/Vancouver" }).format(new Date(v.scheduled_for))}</span></div>
                <div class="grow stack tight" style="gap:0">
                  <span class="strong">{t(`visitType.${v.type}`)}</span>
                  <span class="small muted">{fmt.time(v.scheduled_for)}{v.expert ? ` · ${v.expert}` : ""}</span>
                </div>
                {v.status === "entry_approved" ? <span class="chip good">{t("customer.entryOk")}</span> : <span class="chip">{t(`visitStatus.${v.status}`)}</span>}
              </div>
            ))}
          </div>
        ) : <div class="list"><div class="empty"><span>{t("customer.noUpcoming")}</span></div></div>}

        <div class="section-title">
          <h2>{t("customer.requests")}</h2>
          {p.request ? <Link href="/request" class="small strong">{t("customer.newRequest")}</Link> : null}
        </div>
        {open.length ? (
          <div class="list">
            {open.map((x: any) => (
              <Link class="list-row" href={`/requests/${x.id}`}>
                <div class="grow stack tight" style="gap:0">
                  <span class="strong">{x.title}</span>
                  <span class="small muted">{t(`service.${x.service}`)} · {fmt.date(x.created_at, "short")}</span>
                </div>
                <TicketStatus status={x.status} />
              </Link>
            ))}
          </div>
        ) : <div class="list"><div class="empty"><span>{t("customer.noRequests")}</span>{p.request ? <Link href="/request" class="btn secondary small">{t("customer.newRequest")}</Link> : null}</div></div>}

        {past.length ? (
          <div>
            <div class="section-title"><h2>{t("customer.recentVisits")}</h2></div>
            <div class="list">
              {past.slice(0, 3).map((v: any) => (
                <div class="list-row">
                  <div class="grow stack tight" style="gap:0">
                    <span class="strong">{t(`visitType.${v.type}`)} · {fmt.date(v.scheduled_for, "short")}</span>
                    <span class="small muted">{v.expert ?? ""}</span>
                    {v.summary ? <span class="small" style="margin-top:4px">{v.summary}</span> : null}
                  </div>
                  <HealthChip health={v.outcome ?? "good"} />
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div class="section-title"><h2>{t("customer.yourHome")}</h2></div>
        <div class="list">
          <div class="list-row">
            <Icon name="home" />
            <div class="grow stack tight" style="gap:0">
              <span class="strong">{t(`occupancy.${home.occupancy}`)}</span>
              <span class="small muted">{t("admin.homes.since", { date: fmt.date(home.occupancy_since) })}</span>
            </div>
            {p.setOccupancy ? <Button kind="ghost" size="small" onClick={() => occupancyDialog(home, reload)}>{t("common.change")}</Button> : null}
          </div>
          {home.primary_expert ? (
            <div class="list-row">
              <Icon name="user" />
              <div class="grow stack tight" style="gap:0">
                <span class="strong">{home.primary_expert.name}</span>
                <span class="small muted">{t("customer.yourExpert")}{home.backup_expert ? ` · ${t("customer.backupExpert", { name: home.backup_expert.name })}` : ""}</span>
              </div>
            </div>
          ) : null}
          {home.plan ? (
            <div class="list-row">
              <Icon name="shield" />
              <div class="grow stack tight" style="gap:0">
                <span class="strong">{tl(home.plan.name)}</span>
                <span class="small muted">{t("customer.emergencyCap", { cap: fmt.money(home.emergency_cap_cents) })}</span>
              </div>
            </div>
          ) : null}
        </div>

        {p.killSwitch && !home.access_revoked_at ? (
          <section class="killswitch">
            <div class="row" style="gap:10px"><Icon name="power" /><h2 style="font-size:var(--fs-l)">{t("customer.kill.title")}</h2></div>
            <p class="small muted">{t("customer.kill.body", { app: brand.current.appName })}</p>
            <div><Button kind="danger" busy={isBusy("kill")} onClick={killSwitch}>{t("customer.kill.button")}</Button></div>
          </section>
        ) : null}
      </div>
    );
  }, 6);
}

